import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MARKETING_CTAS } from '@/lib/marketingCtas';
import { answerText, buildFaqGroups } from './faqContent';

const GROUPS = buildFaqGroups();
const FAQ_ITEMS = GROUPS.flatMap((g) => g.entries.map((e) => ({ question: e.q, answer: answerText(e.a).replace(/\s+/g, ' ').trim() })));
const TITLE = 'FAQ | Free SAT Course and Tutoring Questions | Mentara';
const DESCRIPTION = 'Straight answers about the free SAT course, 1-on-1 tutoring, parent accounts and tutor accounts on Mentara.';

export function FaqPage() {
  return (
    <>
      <title>{TITLE}</title>
      <meta name="description" content={DESCRIPTION} />
      <meta name="keywords" content="Mentara FAQ, free SAT course questions, online tutoring questions, tutor accounts, parent accounts" />
      <link rel="canonical" href="https://mentaratutors.com/faq/" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mentaratutors.com/faq/" />
      <meta property="og:title" content={TITLE} />
      <meta property="og:description" content={DESCRIPTION} />
      <meta property="og:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://mentaratutors.com/faq/" />
      <meta property="twitter:title" content={TITLE} />
      <meta property="twitter:description" content={DESCRIPTION} />
      <meta property="twitter:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />

      {/* FAQPage structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQ_ITEMS.map((item) => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer,
          },
        })),
      }) }} />
      
      <div className="bg-background">
        <section className="mx-auto w-full max-w-3xl px-4 pb-6 pt-12 sm:px-6 lg:pt-16">
          <h1 className="text-balance text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">Straight answers.</h1>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
            The free SAT course, tutoring, and what parents and tutors can do. Anything missing, email{' '}
            <a href="mailto:admin@mentaratutors.com" className="underline underline-offset-4 hover:text-foreground">admin@mentaratutors.com</a>.
          </p>
        </section>

        <div className="mx-auto w-full max-w-3xl space-y-10 px-4 pb-14 sm:px-6" data-testid="faq-groups">
          {GROUPS.map((g) => (
            <section key={g.id} aria-labelledby={`faq-${g.id}`}>
              <h2 id={`faq-${g.id}`} className="text-2xl font-semibold tracking-tight">{g.title}</h2>
              <div className="mt-3 divide-y border-y">
                {g.entries.map((e) => (
                  <details key={e.q} className="group py-1">
                    <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded text-base font-semibold outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                      {e.q}
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden />
                    </summary>
                    <div className="max-w-prose pb-3 pt-1 text-[15px] leading-relaxed text-muted-foreground">{e.a}</div>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section className="border-t px-4 py-14 text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Ready to start?</h2>
          <Button size="lg" className="mt-5" asChild>
            <Link to={MARKETING_CTAS.closeCourse.href}>{MARKETING_CTAS.closeCourse.label}</Link>
          </Button>
        </section>
      </div>
    </>
  );
}
